import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ClipboardList, 
  Calendar, 
  Globe,
  CheckCircle,
  Shield,
  AlertTriangle,
  XCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientConsentHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchConsentLog();
  }, []);

  const fetchConsentLog = async () => {
    const token = localStorage.getItem('clientToken');
    if (!token) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/consent-log`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch consent log:', error);
      toast.error('Failed to fetch consent history');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeConsent = async () => {
    if (confirmText !== 'DELETE MY DATA') {
      toast.error('Please type "DELETE MY DATA" to confirm');
      return;
    }

    const token = localStorage.getItem('clientToken');
    if (!token) return;

    setRevoking(true);
    try {
      await axios.post(`${API}/client/revoke-consent`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Your consent has been revoked and all data has been deleted');
      
      // Clear local storage and redirect to login
      localStorage.removeItem('clientToken');
      localStorage.removeItem('clientEmail');
      localStorage.removeItem('clientId');
      localStorage.removeItem('clientName');
      
      // Redirect to home page after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Failed to revoke consent:', error);
      toast.error(error.response?.data?.detail || 'Failed to revoke consent');
      setRevoking(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' IST';
  };

  const formatAction = (action) => {
    switch (action) {
      case 'consent_given':
        return { label: 'Consent Given', color: 'emerald' };
      case 'consent_revoked':
        return { label: 'Consent Revoked', color: 'red' };
      default:
        return { label: action, color: 'slate' };
    }
  };

  return (
    <div className="space-y-6" data-testid="client-consent-history">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Shield className="w-7 h-7 text-amber-400" />
          Consent History
        </h1>
        <p className="text-slate-400 mt-1">
          View your consent records and manage your data
        </p>
      </div>

      {/* Info Card */}
      <Card className="bg-emerald-500/10 border-emerald-500/20">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div>
              <p className="text-emerald-400 font-medium">Your Consent Status</p>
              <p className="text-slate-400 text-sm mt-1">
                You have provided consent for data processing. Your consent is recorded with timestamp and IP address for compliance purposes.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Consent Log */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="border-b border-slate-800">
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-amber-400" />
            Consent Records
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-slate-400">Loading consent history...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No consent records found</p>
              <p className="text-slate-500 text-sm mt-1">
                Your consent history will appear here after you provide consent
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {logs.map((log, index) => {
                const actionInfo = formatAction(log.action);
                return (
                  <div key={index} className="p-4 hover:bg-slate-800/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${actionInfo.color}-500/10 text-${actionInfo.color}-400 border border-${actionInfo.color}-500/20`}>
                            {log.action === 'consent_given' ? (
                              <CheckCircle className="w-3 h-3 mr-1" />
                            ) : (
                              <XCircle className="w-3 h-3 mr-1" />
                            )}
                            {actionInfo.label}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div className="flex items-center gap-2 text-slate-400">
                            <Calendar className="w-4 h-4 text-slate-500" />
                            <span>{formatDate(log.consent_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-slate-400">
                            <Globe className="w-4 h-4 text-slate-500" />
                            <span>IP: <code className="text-amber-400 bg-slate-800 px-1.5 py-0.5 rounded text-xs">{log.ip_address}</code></span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revoke Consent Section */}
      <Card className="bg-red-500/5 border-red-500/20">
        <CardHeader>
          <CardTitle className="text-red-400 text-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Revoke Consent & Delete Data
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
            <p className="text-red-300 text-sm font-medium mb-2">⚠️ Warning: This action is permanent and cannot be undone!</p>
            <p className="text-slate-400 text-sm">
              If you revoke your consent, the following will happen:
            </p>
            <ul className="text-slate-400 text-sm mt-2 space-y-1 ml-4">
              <li>• All your wills will be permanently deleted</li>
              <li>• All your Life Vault entries and files will be deleted</li>
              <li>• Your account and login credentials will be removed</li>
              <li>• You will lose access to all your data immediately</li>
              <li>• Consent logs will be retained for audit purposes only</li>
            </ul>
          </div>
          
          <Button
            onClick={() => setShowRevokeModal(true)}
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300"
            data-testid="revoke-consent-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Revoke Consent & Delete My Data
          </Button>
        </CardContent>
      </Card>

      {/* Privacy Note */}
      <Card className="bg-slate-800/30 border-slate-700/50">
        <CardContent className="p-4">
          <p className="text-slate-500 text-sm">
            <strong className="text-slate-400">Privacy Note:</strong> Your consent records are stored securely and include your IP address at the time of consent for legal compliance. 
            You can withdraw consent at any time using the button above. After revocation, only your consent logs will be retained for audit purposes.
          </p>
        </CardContent>
      </Card>

      {/* Revoke Consent Confirmation Modal */}
      {showRevokeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-red-500/30 rounded-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-8 h-8" />
              <h2 className="text-xl font-bold">Confirm Consent Revocation</h2>
            </div>
            
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
              <p className="text-red-300 text-sm font-medium mb-2">
                This action will permanently delete:
              </p>
              <ul className="text-slate-400 text-sm space-y-1">
                <li>• All your wills and will data</li>
                <li>• All Life Vault entries and uploaded files</li>
                <li>• Your account credentials</li>
                <li>• Access to WillKart.ai</li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                To confirm, type <strong className="text-red-400">DELETE MY DATA</strong> below:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE MY DATA"
                className="w-full px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                data-testid="revoke-confirm-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => {
                  setShowRevokeModal(false);
                  setConfirmText('');
                }}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                disabled={revoking}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRevokeConsent}
                disabled={confirmText !== 'DELETE MY DATA' || revoking}
                className={`flex-1 ${
                  confirmText === 'DELETE MY DATA' 
                    ? 'bg-red-500 hover:bg-red-400 text-white' 
                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                }`}
                data-testid="revoke-confirm-btn"
              >
                {revoking ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Revoke & Delete
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientConsentHistory;
